import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import {
  Alert, Button, TextField, MenuItem, Select, Chip, InputLabel,
  FormControl, CircularProgress, Paper, IconButton,
} from '@mui/material';
import { GuestPushNotificationService } from '../services/GuestPushNotificationService';

const DEEP_LINK_SCREENS = [
  { value: '', label: 'No deep link (just open app)' },
  { value: 'Home', label: 'Home (Services)' },
  { value: 'Bookings', label: 'Bookings' },
  { value: 'Profile', label: 'Profile' },
  { value: 'Chat', label: 'Chat (requires serviceRequestId param)' },
  { value: 'Booking', label: 'Booking detail (requires serviceId param)' },
];

/**
 * Three-step push composer:
 *   1. Pick target (all / segment / specific guests)
 *   2. Compose + live preview count
 *   3. Send with confirmation
 */
export default function GuestPushNotificationCreate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  // Target state
  const [targetType, setTargetType] = useState('all');
  const [segment, setSegment] = useState({
    hasActiveStay: false,
    city: '',
    state: '',
    signedUpInLastDays: '',
    signupSource: '',
  });
  const [guestSearch, setGuestSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedGuests, setSelectedGuests] = useState([]);
  const [searching, setSearching] = useState(false);

  // Message state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deepLinkScreen, setDeepLinkScreen] = useState('');
  const [deepLinkParams, setDeepLinkParams] = useState('');

  // Preview state
  const [preview, setPreview] = useState({ totalMatching: 0, withPushToken: 0 });
  const [previewLoading, setPreviewLoading] = useState(false);

  const target = useMemo(() => {
    if (targetType === 'guestIds') {
      return { type: 'guestIds', guestIds: selectedGuests.map((g) => g._id) };
    }
    if (targetType === 'segment') {
      return {
        type: 'segment',
        segment: {
          hasActiveStay: Boolean(segment.hasActiveStay),
          city: segment.city || '',
          state: segment.state || '',
          signedUpInLastDays: segment.signedUpInLastDays ? Number(segment.signedUpInLastDays) : null,
          signupSource: segment.signupSource || '',
        },
      };
    }
    return { type: 'all' };
  }, [targetType, segment, selectedGuests]);

  useEffect(() => {
    let cancelled = false;
    if (targetType === 'guestIds' && selectedGuests.length === 0) {
      setPreview({ totalMatching: 0, withPushToken: 0 });
      return;
    }
    setPreviewLoading(true);
    GuestPushNotificationService.preview(target)
      .then((data) => {
        if (!cancelled) setPreview({
          totalMatching: data?.totalMatching ?? 0,
          withPushToken: data?.withPushToken ?? 0,
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [target, targetType, selectedGuests.length]);

  const handleSearch = async () => {
    if (!guestSearch.trim()) return;
    setSearching(true);
    try {
      const res = await GuestPushNotificationService.searchGuests(guestSearch.trim(), 25);
      setSearchResults(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const addGuest = (g) => {
    if (selectedGuests.find((x) => x._id === g._id)) return;
    setSelectedGuests((prev) => [...prev, g]);
  };
  const removeGuest = (id) => {
    setSelectedGuests((prev) => prev.filter((g) => g._id !== id));
  };

  const handleSend = async () => {
    setError('');
    if (!title.trim() || !body.trim()) {
      setError('Title and body are required');
      return;
    }
    if (targetType === 'guestIds' && !selectedGuests.length) {
      setError('Select at least one guest');
      return;
    }
    let parsedParams = {};
    if (deepLinkParams.trim()) {
      try { parsedParams = JSON.parse(deepLinkParams); } catch (_) {
        setError('Deep link params must be valid JSON or empty');
        return;
      }
    }

    setSending(true);
    try {
      await GuestPushNotificationService.send({
        title: title.trim(),
        body: body.trim(),
        target,
        deepLink: deepLinkScreen ? { screen: deepLinkScreen, params: parsedParams } : undefined,
      });
      navigate('/guest-push');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex w-full h-screen overflow-auto">
      <div className="flex flex-col w-full mx-auto bg-[#F5F5F5]">
        <div className="px-10 bg-white">
          <AdminHeader heading={'Send Push Notification'} pages={'Pages / Guest Push / Create'} />
        </div>

        <div className="px-10 py-6 flex flex-col gap-6 max-w-4xl">
          {error ? <Alert severity="error">{error}</Alert> : null}

          <div className="flex gap-2">
            {[1, 2, 3].map((n) => (
              <Chip
                key={n}
                color={step === n ? 'primary' : 'default'}
                label={`Step ${n}: ${n === 1 ? 'Target' : n === 2 ? 'Compose' : 'Review'}`}
                onClick={() => setStep(n)}
              />
            ))}
          </div>

          {step === 1 ? (
            <Paper sx={{ p: 3 }}>
              <h3 className="font-semibold mb-3">Who should get this?</h3>
              <div className="flex gap-2 mb-4">
                {['all', 'segment', 'guestIds'].map((t) => (
                  <Chip
                    key={t}
                    label={t === 'all' ? 'All guests' : t === 'segment' ? 'Segment' : 'Specific guests'}
                    color={targetType === t ? 'primary' : 'default'}
                    onClick={() => setTargetType(t)}
                  />
                ))}
              </div>

              {targetType === 'segment' ? (
                <div className="flex flex-col gap-3">
                  <div className="flex gap-3">
                    <TextField
                      label="City"
                      value={segment.city}
                      onChange={(e) => setSegment({ ...segment, city: e.target.value })}
                      fullWidth
                    />
                    <TextField
                      label="State (e.g. CO)"
                      value={segment.state}
                      onChange={(e) => setSegment({ ...segment, state: e.target.value })}
                      sx={{ width: 200 }}
                    />
                  </div>
                  <div className="flex gap-3">
                    <TextField
                      label="Signed up in last N days"
                      type="number"
                      value={segment.signedUpInLastDays}
                      onChange={(e) => setSegment({ ...segment, signedUpInLastDays: e.target.value })}
                      fullWidth
                    />
                    <FormControl fullWidth>
                      <InputLabel>Signup source</InputLabel>
                      <Select
                        label="Signup source"
                        value={segment.signupSource}
                        onChange={(e) => setSegment({ ...segment, signupSource: e.target.value })}
                      >
                        <MenuItem value="">Any</MenuItem>
                        <MenuItem value="host_link">Host link</MenuItem>
                        <MenuItem value="property_code">Property code</MenuItem>
                        <MenuItem value="manual_address">Manual address</MenuItem>
                        <MenuItem value="direct">Direct</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={segment.hasActiveStay}
                      onChange={(e) => setSegment({ ...segment, hasActiveStay: e.target.checked })}
                    />
                    Only guests with an active stay right now
                  </label>
                </div>
              ) : null}

              {targetType === 'guestIds' ? (
                <div>
                  <div className="flex gap-2 mb-3">
                    <TextField
                      fullWidth
                      placeholder="Search by name, email, phone"
                      value={guestSearch}
                      onChange={(e) => setGuestSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button variant="contained" onClick={handleSearch} disabled={searching}>
                      {searching ? '…' : 'Search'}
                    </Button>
                  </div>

                  {selectedGuests.length ? (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {selectedGuests.map((g) => (
                        <Chip
                          key={g._id}
                          label={`${g.firstName} ${g.lastName || ''} (${g.email})`}
                          onDelete={() => removeGuest(g._id)}
                        />
                      ))}
                    </div>
                  ) : null}

                  <div className="max-h-64 overflow-auto border rounded">
                    {searchResults.map((g) => (
                      <div
                        key={g._id}
                        className="flex justify-between items-center px-3 py-2 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                        onClick={() => addGuest(g)}
                      >
                        <div>
                          <div className="font-medium">{g.firstName} {g.lastName}</div>
                          <div className="text-sm text-gray-500">{g.email}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          {g.hasPushToken ? (
                            <Chip size="small" color="success" label="Push ✓" />
                          ) : (
                            <Chip size="small" label="No token" />
                          )}
                          <Button size="small" variant="outlined">Add</Button>
                        </div>
                      </div>
                    ))}
                    {searchResults.length === 0 && (
                      <div className="px-3 py-4 text-gray-400 text-sm text-center">
                        {searching ? 'Searching…' : 'No results. Try a different query.'}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              <div className="mt-4 text-sm text-gray-600">
                {previewLoading ? 'Calculating reach…' : (
                  <>This will reach <strong>{preview.withPushToken}</strong> guests with push notifications enabled
                  {preview.totalMatching > preview.withPushToken && (
                    <> ({preview.totalMatching - preview.withPushToken} more matched but have no push token).</>
                  )}</>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <Button variant="contained" onClick={() => setStep(2)}>Next: Compose</Button>
              </div>
            </Paper>
          ) : null}

          {step === 2 ? (
            <Paper sx={{ p: 3 }}>
              <h3 className="font-semibold mb-3">Compose your message</h3>
              <div className="flex flex-col gap-3">
                <TextField
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                />
                <TextField
                  label="Body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 500 }}
                />
                <FormControl fullWidth>
                  <InputLabel>Deep link screen</InputLabel>
                  <Select
                    label="Deep link screen"
                    value={deepLinkScreen}
                    onChange={(e) => setDeepLinkScreen(e.target.value)}
                  >
                    {DEEP_LINK_SCREENS.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {deepLinkScreen ? (
                  <TextField
                    label='Deep link params (JSON, e.g. {"serviceRequestId":"..."})'
                    value={deepLinkParams}
                    onChange={(e) => setDeepLinkParams(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                  />
                ) : null}
              </div>
              <div className="mt-4 flex justify-between">
                <Button onClick={() => setStep(1)}>Back</Button>
                <Button variant="contained" onClick={() => setStep(3)}>Next: Review</Button>
              </div>
            </Paper>
          ) : null}

          {step === 3 ? (
            <Paper sx={{ p: 3 }}>
              <h3 className="font-semibold mb-3">Review and send</h3>
              <div className="rounded-xl border p-4 bg-gray-50">
                <div className="text-xs uppercase text-gray-500 mb-1">Preview</div>
                <div className="font-semibold">{title || '(no title)'}</div>
                <div className="text-sm text-gray-700">{body || '(no body)'}</div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                Target: <strong>{targetType}</strong> — reaching <strong>{preview.withPushToken}</strong> guests.
              </div>
              <div className="mt-4 flex justify-between">
                <Button onClick={() => setStep(2)}>Back</Button>
                <Button variant="contained" color="primary" onClick={handleSend} disabled={sending}>
                  {sending ? <CircularProgress size={20} /> : `Send to ${preview.withPushToken}`}
                </Button>
              </div>
            </Paper>
          ) : null}
        </div>
      </div>
    </div>
  );
}
